import React, { memo } from "react";

import { classNames, transformDateToLocalDateString } from "helpers";
import { ShowcaseCard, ShowcaseNotice } from "ui/components/Showcase";
import { getVehicleName } from "ui/modules/fleet/vehicles/pages/VehicleDetailPage/components/VehicleDetailCard/components/VehicleEditCard/components/VehicleWizardIntro/helpers";

import { RESIDUAL_VALUE_INTRO_SAMPLE } from "../consts";
import { ResidualValueSceneProps } from "../types";

/** Korak "Detalji vozila": trajanje ugovora i kilometraža od početka do kraja financiranja. */
const ResidualValueDetailsScene: React.FC<ResidualValueSceneProps> = ({ formValues, vehicleInfo }) => {
  const startDate = formValues.financingStartDate ? new Date(formValues.financingStartDate) : null;
  const endDate = formValues.contractEndDate ? new Date(formValues.contractEndDate) : null;

  // Trajanje u mjesecima tek kad su upisana oba datuma, inače primjer.
  const contractMonths =
    startDate && endDate
      ? Math.max((endDate.getFullYear() - startDate.getFullYear()) * 12 + endDate.getMonth() - startDate.getMonth(), 0)
      : RESIDUAL_VALUE_INTRO_SAMPLE.contractMonths;

  const mileageStart = formValues.mileageKm ?? RESIDUAL_VALUE_INTRO_SAMPLE.mileageKm;
  const mileageEnd = formValues.mileageAtContractEnd ?? RESIDUAL_VALUE_INTRO_SAMPLE.mileageAtContractEnd;
  const yearlyMileage = contractMonths > 0 ? Math.round(((mileageEnd - mileageStart) * 12) / contractMonths) : 0;

  const rows = [
    { label: "Početak financiranja", value: startDate ? transformDateToLocalDateString(startDate) : "Datum ugovora" },
    { label: "Istek ugovora", value: endDate ? transformDateToLocalDateString(endDate) : "Kraj ugovora" },
    { label: "Kilometraža sada", value: `${mileageStart.toLocaleString("hr-HR")} km` },
    { label: "Na kraju ugovora", value: `${mileageEnd.toLocaleString("hr-HR")} km` },
  ];

  return (
    <>
      <ShowcaseCard className="left-[0.5rem] top-[-1rem] w-[17rem] h-[18rem]" delay={0}>
        <div className="p-5">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-400">Ugovor</span>
            <span
              className={classNames(
                "px-2 py-0.5 rounded-full text-[0.6rem] font-semibold",
                formValues.usedVehicle ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"
              )}
            >
              {formValues.usedVehicle ? "Rabljeno vozilo" : "Novo vozilo"}
            </span>
          </div>
          <div className="text-sm font-bold text-slate-800 truncate">{getVehicleName(vehicleInfo)}</div>
          <div className="mt-3 space-y-2">
            {rows.map((row) => (
              <div key={row.label} className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2">
                <span className="text-[0.7rem] text-slate-500">{row.label}</span>
                <span className="text-[0.7rem] font-semibold text-slate-800">{row.value}</span>
              </div>
            ))}
          </div>
        </div>
      </ShowcaseCard>

      <ShowcaseCard className="right-[-0.5rem] top-[6rem] w-[14rem]" delay={0.25}>
        <div className="p-5">
          <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-400">Trajanje ugovora</div>
          <div className="mt-2 text-xl font-bold text-slate-800">{contractMonths} mj.</div>
          {/* Traka pokazuje trajanje ugovora u odnosu na pet godina. */}
          <div className="mt-3 h-1.5 w-full rounded-full bg-slate-100">
            <div className="h-1.5 rounded-full bg-amber-400" style={{ width: `${Math.min(Math.max((contractMonths / 60) * 100, 10), 100)}%` }} />
          </div>
          <div className="mt-1 text-[0.6rem] text-slate-400">
            Oko {yearlyMileage.toLocaleString("hr-HR")} km godišnje
          </div>
        </div>
      </ShowcaseCard>

      <ShowcaseCard className="left-[-1.5rem] bottom-[-0.5rem] w-[15rem]" delay={0.5}>
        <ShowcaseNotice
          title="Vrijednost na kraju ugovora"
          subtitle="Ovisi o starosti i kilometrima"
          iconClassName="bg-amber-100"
          icon={
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="#B45309" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <path d="M16 2v4M8 2v4M3 10h18" />
            </svg>
          }
        />
      </ShowcaseCard>
    </>
  );
};

export default memo(ResidualValueDetailsScene);
